import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import Pagination from "components/common/Pagination";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import { LoginContext } from "helpers/LoginContext";
import { apiGetRequest } from "helpers/Requests";
import {
  SETTINGS_BREADCRUMB_PAGES,
  SP_SETTING_BREADCRUMB,
} from "Constants/BreadcrumbConstant";
import { USER_ROLE_TITLE } from "Constants/constant";
import { formatDateTimeInClientTimezone } from "Utils/utils";
import {
  URL_SP_AI_SUMMARY,
  URL_SP_AI_USAGE,
  URL_SP_AI_USAGE_FILTERS,
  URL_SP_AI_WALLET,
  URL_SP_AI_TOP_UP_STATUS,
} from "store/services/URL";
import { buildFeatureFilterOptions, formatFeatureLabel } from "./aiUsageLabels";
import { buildQuery, formatAud, formatAudSmart } from "./aiUsageChartUtils";
import { parseBalanceAud, applyWalletBalance } from "./topUpBalanceUtils";
import AiUsageByFeatureChart from "./AiUsageByFeatureChart";
import AiCostOverTimeChart from "./AiCostOverTimeChart";
import AiTopUpModal from "./AiTopUpModal";
import {
  AiWalletBalanceSkeleton,
  AiWalletCardSkeleton,
  AiPeriodSummaryMetricsSkeleton,
  AiUsageTableBodySkeleton,
} from "./AiCreditsUsageSkeleton";
import {
  formatUserRoleLabel,
  UsageRoleBadge,
  UsageStatusBadge,
} from "./AiUsageBadges";
import { FiDownload } from "react-icons/fi";
import {
  HiOutlineSparkles,
  HiOutlineCreditCard,
  HiOutlineChartBar,
  HiOutlineLightningBolt,
} from "react-icons/hi";

const PERIOD_PRESETS = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "mtd", label: "Month to date", type: "mtd" },
];

const toLocalDateString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getPresetRange = (preset) => {
  const to = new Date();
  if (preset.type === "mtd") {
    const from = new Date(to.getFullYear(), to.getMonth(), 1);
    return { from: toLocalDateString(from), to: toLocalDateString(to) };
  }
  const from = new Date();
  from.setDate(from.getDate() - (preset.days || 7));
  return { from: toLocalDateString(from), to: toLocalDateString(to) };
};

const formatTokens = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

const getUserRoleLabel = (row) => formatUserRoleLabel(row, USER_ROLE_TITLE);

const AiCreditsUsage = () => {
  const navigate = useNavigate();
  const { sideBarPadding } = useContext(LoginContext);
  const defaultRange = useMemo(() => getPresetRange(PERIOD_PRESETS[0]), []);

  const [activePreset, setActivePreset] = useState("7d");
  const [dateRange, setDateRange] = useState(defaultRange);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);

  const [userFilter, setUserFilter] = useState("");
  const [featureFilter, setFeatureFilter] = useState("");
  const [filterOptions, setFilterOptions] = useState({ users: [], features: [] });

  const [walletLoading, setWalletLoading] = useState(true);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [error, setError] = useState("");

  const [wallet, setWallet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [usageList, setUsageList] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  const summaryParams = useMemo(() => ({}), []);

  const queryParams = useMemo(
    () => ({
      from: dateRange.from,
      to: dateRange.to,
      page,
      per_page: perPage,
      ...(userFilter ? { user_filter: userFilter } : {}),
      ...(featureFilter ? { feature_filter: featureFilter } : {}),
    }),
    [dateRange.from, dateRange.to, page, perPage, userFilter, featureFilter]
  );

  const fetchWallet = useCallback(async () => {
    setWalletLoading(true);
    try {
      const res = await apiGetRequest(URL_SP_AI_WALLET);
      const payload = res?.data?.data ?? res?.data;
      setWallet(payload?.wallet ?? null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load AI wallet balance."
      );
    } finally {
      setWalletLoading(false);
    }
  }, []);

  const handleTopUpProcessingStart = useCallback(() => {
    setBalanceRefreshing(true);
  }, []);

  const handleTopUpProcessingEnd = useCallback(() => {
    setBalanceRefreshing(false);
  }, []);

  const resolveBalanceAfterTopUp = useCallback(async (detail) => {
    const paymentIntentId = detail?.payment_intent_id;
    let balance = parseBalanceAud(detail?.balance_aud);

    if (balance != null) {
      return balance;
    }

    if (paymentIntentId) {
      for (let attempt = 0; attempt < 6; attempt += 1) {
        try {
          const res = await apiGetRequest(
            `${URL_SP_AI_TOP_UP_STATUS}?payment_intent_id=${encodeURIComponent(paymentIntentId)}`
          );
          const payload = res?.data?.data ?? res?.data;
          if (payload?.credited) {
            balance = parseBalanceAud(payload.balance_aud);
            if (balance != null) {
              return balance;
            }
          }
        } catch (err) {
          // Retry until fallback wallet fetch below.
        }

        if (attempt < 5) {
          await new Promise((resolve) => setTimeout(resolve, 600));
        }
      }
    }

    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const res = await apiGetRequest(URL_SP_AI_WALLET);
        const payload = res?.data?.data ?? res?.data;
        balance = parseBalanceAud(payload?.wallet?.balance_aud);
        if (balance != null) {
          return balance;
        }
      } catch (err) {
        // Retry below.
      }

      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 750));
      }
    }

    return null;
  }, []);

  const handleTopUpSuccess = useCallback(async (detail) => {
    setBalanceRefreshing(true);

    try {
      const balance = await resolveBalanceAfterTopUp(detail);
      if (balance != null) {
        setWallet((prev) => applyWalletBalance(prev, balance));
      }
    } finally {
      setBalanceRefreshing(false);
    }
  }, [resolveBalanceAfterTopUp]);

  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const res = await apiGetRequest(
        `${URL_SP_AI_USAGE}${buildQuery(queryParams)}`
      );
      const payload = res?.data?.data ?? res?.data;
      setUsageList({
        data: payload?.data ?? [],
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        total: payload?.total ?? 0,
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load AI usage history."
      );
    } finally {
      setUsageLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  useEffect(() => {
    let cancelled = false;
    apiGetRequest(URL_SP_AI_USAGE_FILTERS)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        setFilterOptions({
          users: Array.isArray(payload?.users) ? payload.users : [],
          features: Array.isArray(payload?.features) ? payload.features : [],
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);

    apiGetRequest(
      `${URL_SP_AI_SUMMARY}${buildQuery(summaryParams)}`
    )
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        setSummary(payload ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load AI usage summary."
        );
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [summaryParams]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const handlePreset = (preset) => {
    setActivePreset(preset.id);
    setDateRange(getPresetRange(preset));
    setPage(1);
  };

  const handleDateRangeChange = ({ fromDate, toDate }) => {
    if (!fromDate || !toDate) return;
    setActivePreset("custom");
    setDateRange({ from: fromDate, to: toDate });
    setPage(1);
  };

  const handleDateRangeCancel = () => {
    setActivePreset(PERIOD_PRESETS[0].id);
    setDateRange(getPresetRange(PERIOD_PRESETS[0]));
    setPage(1);
  };

  const handleUserFilterChange = (e) => {
    setUserFilter(e.target.value);
    setPage(1);
  };

  const handleFeatureFilterChange = (e) => {
    setFeatureFilter(e.target.value);
    setPage(1);
  };

  const featureOptions = useMemo(
    () => buildFeatureFilterOptions(filterOptions.features),
    [filterOptions.features]
  );

  const balanceAud = wallet?.balance_aud ?? 0;
  const isLowBalance = balanceAud <= 0;

  const handleExportCsv = () => {
    const rows = usageList.data || [];
    if (!rows.length) return;

    const headers = [
      "Date",
      "User",
      "Role",
      "Feature",
      "Input Tokens",
      "Output Tokens",
      "Cost (AUD)",
      "Status",
    ];

    const escape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

    const lines = [
      headers.join(","),
      ...rows.map((row) =>
        [
          formatDateTimeInClientTimezone(row.created_at),
          row.user_name || row.user_email || "—",
          getUserRoleLabel(row) || "—",
          formatFeatureLabel(row),
          row.input_tokens ?? 0,
          row.output_tokens ?? 0,
          row.cost_aud ?? 0,
          row.status || "—",
        ]
          .map(escape)
          .join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ai-usage-${dateRange.from}-to-${dateRange.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const usageRows = usageList.data || [];

  return (
    <div className="min-h-screen bg-gray-100">
      <SideBar />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        } flex flex-col`}
      >
        <Header
          title="AI Credits & Usage"
          // desc="Monitor your AI credit balance and usage across Vertex360 features"
          breadcrumb={
            <Breadcrumb
              pages={SP_SETTING_BREADCRUMB(
                SETTINGS_BREADCRUMB_PAGES.AI_CREDITS_USAGE
              )}
            />
          }
        />

        <section className="container mx-auto sm:px-6 lg:px-8 mt-3 pb-10">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Wallet + period summary */}
          <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch mb-6">
            {walletLoading ? (
              <AiWalletCardSkeleton />
            ) : (
              <div className="lg:col-span-2 rounded-xl border border-blue-200 bg-gradient p-6 text-white shadow-md flex flex-col">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-blue-100 flex items-center gap-2">
                      <HiOutlineSparkles className="h-4 w-4 shrink-0" />
                      Available AI credits
                    </p>
                    <div className="mt-2 h-11 flex items-center">
                      {balanceRefreshing ? (
                        <AiWalletBalanceSkeleton />
                      ) : (
                        <p className="text-4xl font-bold tracking-tight leading-none">
                          {formatAud(balanceAud, 2)}
                          <span className="ml-2 text-lg font-normal text-blue-200">
                            AUD
                          </span>
                        </p>
                      )}
                    </div>
                    <p className="mt-3 text-sm text-blue-100 max-w-lg">
                      Credits are consumed when your team uses AI features such as
                      timesheet anomaly checks and draft notes. Top up anytime to
                      keep AI features available.
                    </p>
                  </div>
                  <div className="shrink-0">
                    <button
                      type="button"
                      onClick={() => setIsTopUpOpen(true)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#4B6CB7] shadow-sm transition-colors hover:bg-blue-50"
                    >
                      <HiOutlineCreditCard className="h-7 w-7 shrink-0" />
                      Top up
                    </button>
                  </div>
                </div>
                {!balanceRefreshing && isLowBalance && (
                  <div className="mt-4 rounded-lg bg-white/10 px-4 py-3 text-sm text-blue-100 border border-white/20 flex flex-wrap items-center justify-between gap-3">
                    <span>
                      Your AI credit balance is empty. AI features will be
                      unavailable until credits are added to your wallet.
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsTopUpOpen(true)}
                      className="inline-flex shrink-0 items-center rounded-md bg-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/30"
                    >
                      Top up now
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex flex-col">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Total period
              </p>
              <div className="mt-1 min-h-[7.25rem] flex flex-col justify-center">
                {summaryLoading ? (
                  <AiPeriodSummaryMetricsSkeleton />
                ) : (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">Spent</p>
                      <p className="mt-1 text-2xl font-semibold text-gray-900 leading-none">
                        {formatAudSmart(summary?.spent_aud ?? 0)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-lg bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">AI calls</p>
                        <p className="font-semibold text-gray-900">
                          {summary?.call_count ?? 0}
                        </p>
                      </div>
                      <div className="rounded-lg bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Total tokens</p>
                        <p className="font-semibold text-gray-900">
                          {formatTokens(
                            (summary?.input_tokens ?? 0) +
                              (summary?.output_tokens ?? 0)
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigate("/settings/ai-settings")}
                className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-[#ECF0FD] px-4 py-2.5 text-sm font-semibold text-[#4B6CB7] transition-colors hover:border-blue-300 hover:bg-blue-50"
              >
                <HiOutlineSparkles className="h-4 w-4 shrink-0" />
                Manage AI settings
              </button>
            </div>
          </div>

          {/* Usage analytics */}
          <div className="mb-6">
            <div className="mb-4 flex items-center gap-2">
              <HiOutlineChartBar className="h-5 w-5 text-[#4B6CB7]" />
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Usage analytics
                </h2>
                <p className="text-sm text-gray-500">
                  Each chart has its own period filter
                </p>
              </div>
            </div>
            <div className="grid min-w-0 items-start gap-5 xl:grid-cols-2">
              <AiUsageByFeatureChart />
              <AiCostOverTimeChart />
            </div>
          </div>

          {/* Prepaid top-up */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ECF0FD] text-[#4B6CB7]">
                <HiOutlineLightningBolt className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Prepaid AI credits
                </p>
                <p className="text-sm text-gray-500">
                  Add credits whenever you need them. Your balance updates once
                  payment is complete.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsTopUpOpen(true)}
              className="cursor-pointer inline-flex items-center rounded-lg bg-[#4B6CB7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3d5a9e]"
            >
              Top up credits
            </button>
          </div>

          {/* Usage table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <HiOutlineChartBar className="h-5 w-5 text-[#4B6CB7] shrink-0" />
                <h2 className="text-base font-semibold text-gray-900">
                  Usage history
                </h2>
              </div>
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={!usageRows.length || usageLoading}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiDownload className="h-4 w-4" />
                <span className="hidden sm:inline">Export CSV</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>

            <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50/60">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:flex-wrap">
                {/* Quick range pills */}
                <div className="min-w-0 shrink-0">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Quick range
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {PERIOD_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handlePreset(preset)}
                        className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                          activePreset === preset.id
                            ? "bg-gradient text-white"
                            : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Date range */}
                <div className="ai-usage-date-filter min-w-0 w-full sm:w-auto sm:min-w-[230px] [&_.react-datepicker-wrapper]:block [&_.react-datepicker-wrapper]:w-full [&_input.did-floating-input]:!h-[38px] [&_input.did-floating-input]:!min-h-[38px] [&_input.did-floating-input]:!rounded-md [&_input.did-floating-input]:!border-gray-300 [&_input.did-floating-input]:!py-0 [&_input.did-floating-input]:!text-sm [&_input.did-floating-input]:!shadow-none [&_img]:!top-1/2 [&_img]:!-translate-y-1/2">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Date range
                  </label>
                  <DateRangePickerNew
                    placeholderText
                    isClearable={false}
                    timesheet
                    cssClass="w-full"
                    fromDate={dateRange.from}
                    toDate={dateRange.to}
                    maxDate={new Date()}
                    handleChange={handleDateRangeChange}
                    handleCancel={handleDateRangeCancel}
                    setPage={setPage}
                  />
                </div>

                {/* User filter */}
                <div className="min-w-0 w-full sm:w-auto sm:min-w-[170px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    User
                  </label>
                  <select
                    value={userFilter}
                    onChange={handleUserFilterChange}
                    className="block w-full h-[38px] rounded-md border border-gray-300 bg-white px-3 pr-8 text-sm text-gray-700 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 cursor-pointer"
                  >
                    <option value="">All users</option>
                    {filterOptions.users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Feature filter */}
                <div className="min-w-0 w-full sm:w-auto sm:min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Feature
                  </label>
                  <select
                    value={featureFilter}
                    onChange={handleFeatureFilterChange}
                    className="block w-full h-[38px] rounded-md border border-gray-300 bg-white px-3 pr-8 text-sm text-gray-700 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 cursor-pointer"
                  >
                    <option value="">All features</option>
                    {featureOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear filters */}
                {(userFilter || featureFilter) && (
                  <div className="shrink-0 self-start sm:self-end sm:ml-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setUserFilter("");
                        setFeatureFilter("");
                        setPage(1);
                      }}
                      className="inline-flex h-[38px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      ✕ Clear filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      "Date / time",
                      "User",
                      "Feature",
                      // "Tokens",
                      "Cost (AUD)",
                      "Status",
                    ].map((col) => (
                      <th
                        key={col}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {usageLoading ? (
                    <AiUsageTableBodySkeleton />
                  ) : usageRows.length > 0 ? (
                    usageRows.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-[#ECF0FD]/40 transition-colors"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {formatDateTimeInClientTimezone(row.created_at)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <div className="flex items-center gap-2.5 min-w-0 max-w-[300px]">
                            <span className="truncate text-sm font-medium text-gray-900">
                              {row.user_name || row.user_email || "—"}
                            </span>
                            <UsageRoleBadge label={getUserRoleLabel(row)} />
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {formatFeatureLabel(row)}
                        </td>
                        {/* <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          <span className="text-gray-500">
                            {formatTokens(row.input_tokens)} in
                          </span>
                          <span className="mx-1 text-gray-300">/</span>
                          <span className="text-gray-500">
                            {formatTokens(row.output_tokens)} out
                          </span>
                        </td> */}
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                          {formatAud(row.cost_aud)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <UsageStatusBadge status={row.status} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-12 px-4 text-center">
                        <HiOutlineSparkles className="mx-auto h-10 w-10 text-gray-300" />
                        <p className="mt-3 text-gray-600 font-medium">
                          No AI usage in this period
                        </p>
                        <p className="mt-1 text-sm text-gray-400 max-w-md mx-auto">
                          Usage will appear here when your team runs AI features
                          such as timesheet anomaly checks. Try expanding the
                          date range.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {usageRows.length > 0 && (
              <div className="border-t border-gray-100 px-4 py-4">
                <Pagination
                  page={usageList.current_page}
                  activeListLength={usageRows}
                  limit={perPage}
                  totalList={usageList.total}
                  totalPages={usageList.last_page}
                  handlePagination={(nextPage) => setPage(nextPage)}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <AiTopUpModal
        isOpen={isTopUpOpen}
        onClose={() => setIsTopUpOpen(false)}
        onProcessingStart={handleTopUpProcessingStart}
        onProcessingEnd={handleTopUpProcessingEnd}
        onSuccess={handleTopUpSuccess}
        minAmount={5}
        maxAmount={500}
      />
    </div>
  );
};

export default AiCreditsUsage;
